import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import AgentAvatar from "../AgentAvatar";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import {
  FormSurface,
  StatusPill,
  useFormPalette,
} from "../forms/DynamicFormPrimitives";
import { NodeReadOnlyField, NodeTextField } from "../../NodeUI";
import { parseMaybeJson } from "./conversationUtils";
import useAgentStore from "../useAgentStore";
import GuideProgressBar from "./humanGuide/GuideProgressBar";
import GuideStepList from "./humanGuide/GuideStepList";
import GuideOptionRow from "./humanGuide/GuideOptionRow";
import {
  STATUS_CANCELLED,
  STATUS_COMPLETED,
  applyGuideSnapshot,
  deriveGuideView,
  formatCapturedLabel,
  isSubmitValueUnchanged,
  resolveViewIndexAfterSnapshot,
  submitValueForStep,
} from "./humanGuide/guideViewModel";

/**
 * Inline card for the agent's `guide_human` tool. Accordion-style step list: question and controls
 * expand inside the active step row. Next|Finish lives in the step; Cancel is in the header. All
 * guide interactions (snapshot fetch, advance, complete, cancel) go through `useAgentStore`.
 */
export default function HumanGuideCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  chatSessionId,
  onOpenAgentSettings = null,
}) {
  const palette = useFormPalette();
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);

  const guideId = parsedResult?.guideId || parsedArgs?.guideId || null;
  const title = parsedResult?.title || parsedArgs?.title || "Guided steps";
  const steps = useMemo(() => {
    const s = parsedResult?.steps || parsedArgs?.steps;
    return Array.isArray(s) ? s : [];
  }, [parsedResult, parsedArgs]);

  const terminalStatus =
    parsedResult?.status === STATUS_COMPLETED ||
    parsedResult?.status === STATUS_CANCELLED
      ? parsedResult.status
      : null;
  const isTerminal = Boolean(terminalStatus);

  const [currentStepIndex, setCurrentStepIndex] = useState(
    typeof parsedResult?.currentStepIndex === "number"
      ? parsedResult.currentStepIndex
      : -1
  );
  const [stepValues, setStepValues] = useState(parsedResult?.stepValues || {});
  const [viewingStepIndex, setViewingStepIndex] = useState(0);
  const [inputDraft, setInputDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const canAct = Boolean(guideId) && !isTerminal && !submitting;

  const view = useMemo(
    () =>
      deriveGuideView({
        steps,
        currentStepIndex,
        stepValues,
        isTerminal,
        terminalStatus,
      }),
    [steps, currentStepIndex, stepValues, isTerminal, terminalStatus]
  );
  const {
    frontierIndex,
    awaitingCompleteOnly,
    maxViewIndex,
    effectiveCurrentStepIndex,
    completedCount,
  } = view;

  const viewingStep = steps[viewingStepIndex] || null;
  const isViewingCompleted =
    viewingStepIndex >= 0 && viewingStepIndex <= effectiveCurrentStepIndex;
  const isViewingLastStep = viewingStepIndex === steps.length - 1;
  const stepReadOnly = !canAct;

  useEffect(() => {
    if (!viewingStep) {
      setInputDraft("");
      return;
    }
    const { type, stepId } = viewingStep;
    const captured = stepValues[stepId];
    if (type === "paste_input" || type === "single_choice") {
      setInputDraft(captured != null ? String(captured) : "");
    } else {
      setInputDraft("");
    }
    setCopied(false);
  }, [viewingStep, stepValues]);

  useEffect(() => {
    setViewingStepIndex((prev) => {
      if (steps.length === 0) return 0;
      return Math.min(Math.max(prev, 0), maxViewIndex);
    });
  }, [maxViewIndex, steps.length]);

  useEffect(() => {
    if (!guideId || !chatSessionId || isTerminal) return;
    let cancelled = false;
    (async () => {
      const snapshot = await useAgentStore
        .getState()
        .fetchHumanGuideSnapshot(chatSessionId, guideId);
      if (cancelled || !snapshot) return;
      applyGuideSnapshot(snapshot, { setCurrentStepIndex, setStepValues });
      setViewingStepIndex(resolveViewIndexAfterSnapshot(snapshot, steps));
    })();
    return () => {
      cancelled = true;
    };
  }, [guideId, chatSessionId, isTerminal, steps]);

  useEffect(() => {
    if (!isTerminal || !parsedResult) return;
    if (parsedResult.stepValues && typeof parsedResult.stepValues === "object") {
      setStepValues(parsedResult.stepValues);
    }
    if (typeof parsedResult.currentStepIndex === "number") {
      setCurrentStepIndex(parsedResult.currentStepIndex);
    }
  }, [isTerminal, parsedResult]);

  const avatarClick =
    agentId && typeof onOpenAgentSettings === "function"
      ? (e) => {
          e.stopPropagation();
          onOpenAgentSettings(agentId);
        }
      : undefined;

  const handleStepSelect = useCallback(
    (index) => {
      if (index >= 0 && index <= maxViewIndex) {
        setViewingStepIndex(index);
      }
    },
    [maxViewIndex]
  );

  const advance = async (stepId, value) => {
    if (!canAct || !stepId) return null;
    setSubmitting(true);
    setError(null);
    try {
      const snapshot = await useAgentStore
        .getState()
        .advanceHumanGuide(chatSessionId, { guideId, stepId, value });
      if (!snapshot) {
        setError("Couldn't save that step. Please try again.");
        setSubmitting(false);
        return null;
      }
      applyGuideSnapshot(snapshot, { setCurrentStepIndex, setStepValues });
      setSubmitting(false);
      return snapshot;
    } catch (e) {
      setError("Couldn't save that step. Please try again.");
      setSubmitting(false);
      return null;
    }
  };

  const finishGuide = async (cancel) => {
    if (!guideId || isTerminal || submitting) {
      return false;
    }
    setSubmitting(true);
    setError(null);
    try {
      const store = useAgentStore.getState();
      const ok = cancel
        ? await store.cancelHumanGuide(chatSessionId, agentId, { guideId })
        : await store.completeHumanGuide(chatSessionId, agentId, { guideId });
      if (ok === false) {
        setError("Couldn't finish the guide. Please try again.");
        setSubmitting(false);
        return false;
      }
      return true;
    } catch (e) {
      setError("Couldn't finish the guide. Please try again.");
      setSubmitting(false);
      return false;
    }
  };

  const completeGuide = () => finishGuide(false);
  const cancelGuide = () => finishGuide(true);

  const advanceThenComplete = async (stepId, value) => {
    if (awaitingCompleteOnly) {
      return completeGuide();
    }
    const snapshot = await advance(stepId, value);
    // `advance` already surfaced an inline error on failure; do not attempt to complete a guide
    // whose final required step was never captured (the server would reject it with a second error).
    if (!snapshot) return false;
    return completeGuide();
  };

  const handleStepSubmit = async (step) => {
    if (!step?.stepId) return;

    if (
      isViewingCompleted &&
      isSubmitValueUnchanged(step, stepValues, inputDraft)
    ) {
      if (viewingStepIndex < maxViewIndex) {
        setViewingStepIndex((prev) => prev + 1);
      } else if (awaitingCompleteOnly) {
        await completeGuide();
      }
      return;
    }

    const value = submitValueForStep(step, inputDraft);

    if (
      isViewingLastStep &&
      (viewingStepIndex === frontierIndex || awaitingCompleteOnly)
    ) {
      await advanceThenComplete(step.stepId, value);
      return;
    }

    const snapshot = await advance(step.stepId, value);
    if (snapshot) {
      setViewingStepIndex(resolveViewIndexAfterSnapshot(snapshot, steps));
    }
  };

  const isFinishStep =
    isViewingLastStep &&
    (viewingStepIndex === frontierIndex || awaitingCompleteOnly);

  const primaryLabel = isFinishStep
    ? "Finish"
    : viewingStep?.type === "confirm"
      ? viewingStep.confirmLabel || "Next"
      : "Next";

  const nextDisabled = (() => {
    if (!viewingStep) return true;
    if (viewingStep.type === "paste_input") {
      const trimmed = inputDraft.trim();
      return viewingStep.required !== false && trimmed.length === 0;
    }
    if (viewingStep.type === "single_choice") {
      return !inputDraft;
    }
    return false;
  })();

  const handleCopyValue = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const renderStepContent = (step) => {
    if (!step) return null;
    const { type, body, stepId } = step;
    const captured = stepValues?.[stepId];
    const displayValue =
      captured != null ? formatCapturedLabel(step, captured) : inputDraft;

    return (
      <Box>
        {body && (
          <Typography
            sx={{
              fontSize: "13px",
              color: palette.textPrimary,
              lineHeight: 1.55,
              mb: 1.25,
              whiteSpace: "pre-wrap",
            }}
          >
            {body}
          </Typography>
        )}

        {type === "open_url" && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => window.open(step.url || "", "_blank", "noopener")}
            sx={{
              textTransform: "none",
              fontSize: "13px",
              borderColor: palette.chipBorder,
              color: palette.textPrimary,
              mb: 0.5,
            }}
          >
            {step.linkLabel || "Open link"}
          </Button>
        )}

        {type === "copy_value" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "stretch" },
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NodeReadOnlyField
                label={step.valueLabel || "Copy this value"}
                value={step.value || ""}
              />
            </Box>
            <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
              <IconButton
                size="small"
                onClick={() => handleCopyValue(step.value || "")}
                sx={{
                  alignSelf: { xs: "flex-end", sm: "flex-end" },
                  mb: { xs: 0, sm: 0.25 },
                  border: `1px solid ${palette.chipBorder}`,
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                }}
              >
                {copied ? (
                  <CheckRoundedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {type === "paste_input" &&
          (stepReadOnly ? (
            <NodeReadOnlyField
              label={step.inputLabel || "Your response"}
              value={displayValue || "—"}
            />
          ) : (
            <NodeTextField
              label={step.inputLabel || undefined}
              placeholder={step.placeholder || "Paste or type here…"}
              value={inputDraft}
              onChange={setInputDraft}
              multiline={Boolean(step.multiline)}
              minRows={step.multiline ? 3 : 1}
            />
          ))}

        {type === "single_choice" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {(Array.isArray(step.options) ? step.options : []).map((opt) => (
              <GuideOptionRow
                key={opt}
                label={opt}
                selected={(stepReadOnly ? captured : inputDraft) === opt}
                disabled={stepReadOnly}
                onClick={() => setInputDraft(opt)}
                palette={palette}
              />
            ))}
          </Box>
        )}

        {(type === "confirm" || type === "instruction") &&
          stepReadOnly &&
          captured && (
            <Typography sx={{ fontSize: "13px", color: palette.textSecondary }}>
              {formatCapturedLabel(step, captured)}
            </Typography>
          )}

        {!stepReadOnly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1.25,
            }}
          >
            {submitting && (
              <CircularProgress size={14} sx={{ color: palette.accent }} />
            )}
            <Button
              size="small"
              variant="contained"
              disabled={!canAct || nextDisabled || submitting}
              onClick={() => handleStepSubmit(step)}
              disableElevation
              sx={{
                textTransform: "none",
                fontSize: "12px",
                backgroundColor: palette.accent,
                "&:hover": { backgroundColor: palette.accentHover },
              }}
            >
              {primaryLabel}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const headerRightSlot = isTerminal ? (
    <StatusPill
      label={terminalStatus === STATUS_CANCELLED ? "Cancelled" : "Done"}
      tone={terminalStatus === STATUS_CANCELLED ? "neutral" : "success"}
    />
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      {submitting && (
        <CircularProgress size={14} sx={{ color: palette.accent }} />
      )}
      <Button
        size="small"
        disabled={!canAct}
        onClick={() => cancelGuide()}
        sx={{
          textTransform: "none",
          fontSize: "12px",
          color: palette.textSecondary,
          minWidth: 0,
        }}
      >
        Cancel
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 2.5 },
        py: 0.4,
        pt: grouped ? 0.4 : 1.5,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 0.75, sm: 1.25 },
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {!grouped && (
          <AgentAvatar
            agentId={agentId}
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
            sx={{ flexShrink: 0 }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <FormSurface
            title={title}
            subtitle={`Step ${viewingStepIndex + 1} of ${steps.length}`}
            rightSlot={headerRightSlot}
          >
            {terminalStatus === STATUS_CANCELLED && (
              <Typography
                sx={{ fontSize: "13px", color: palette.textSecondary, mb: 1 }}
              >
                You cancelled this guide.
              </Typography>
            )}

            <GuideProgressBar
              completedCount={completedCount}
              total={steps.length}
              palette={palette}
            />

            <GuideStepList
              steps={steps}
              currentStepIndex={effectiveCurrentStepIndex}
              viewingStepIndex={viewingStepIndex}
              maxViewIndex={maxViewIndex}
              stepValues={stepValues}
              isTerminal={isTerminal}
              onSelect={handleStepSelect}
              renderActiveStep={renderStepContent}
              palette={palette}
              activeStepLabel={isTerminal ? "Viewing" : "Current"}
            />

            {error && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: palette.errorText,
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {error}
              </Typography>
            )}
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
